import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

// Staggered Word Reveal using native IntersectionObserver
const WordReveal = ({ children, className }) => {
  const [inView, setInView] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true)
        observer.disconnect()
      }
    }, { threshold: 0.1 })
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  const words = typeof children === 'string' ? children.split(' ') : []

  return (
    <span ref={ref} className={`flex flex-wrap ${className}`}>
      {words.map((word, idx) => (
        <span key={idx} className="inline-block overflow-hidden mr-[0.25em] last:mr-0">
          <span
            style={{
              transitionDelay: `${idx * 60}ms`,
              willChange: 'transform, opacity'
            }}
            className={`inline-block transition-all duration-700 ease-out transform ${inView ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
              }`}
          >
            {word}
          </span>
        </span>
      ))}
    </span>
  )
}

// Fade & Slide-up Reveal using native IntersectionObserver
const ScrollReveal = ({ children, delay = 0, className, as: Component = 'div' }) => {
  const [inView, setInView] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true)
        observer.disconnect()
      }
    }, { threshold: 0.05 })
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  return (
    <Component
      ref={ref}
      style={{
        transitionDelay: `${delay}ms`,
        willChange: 'transform, opacity'
      }}
      className={`transition-all duration-700 ease-out transform ${inView ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
        } ${className}`}
    >
      {children}
    </Component>
  )
}

const RabiesHub = () => {
  return (
    <section
      id="awareness"
      className="py-6 sm:py-8 md:py-12 2xl:py-20 px-0 sm:px-6 lg:px-8 2xl:px-16 bg-[#fdfbf7] border-t border-slate-100/50 scroll-mt-12 select-none"
    >
      <div className="max-w-8xl mx-auto">
        <div className="relative">

          {/* Header Overlay */}
          <div className="flex justify-between items-center px-4 sm:px-0 mt-1 sm:mt-4 w-full sm:w-[90%] md:w-[85%] sm:relative sm:absolute lg:top-4 md:top-2 sm:top-0 z-10 select-none">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <ScrollReveal
                as="span"
                delay={0}
                className="text-xs sm:text-sm 2xl:text-base font-bold tracking-wider text-slate-500 uppercase"
              >
                AWARENESS HUB
              </ScrollReveal>
            </div>
          </div>

          {/* Large Image - full edge-to-edge visible strip on mobile, rounded card on desktop */}
          <ScrollReveal
            as="figure"
            delay={100}
            className="relative group overflow-hidden rounded-none sm:rounded-[32px] shadow-none sm:shadow-md border-0 sm:border border-slate-100/50 mt-2 sm:mt-6 w-full"
          >
            <Link to="/awareness" className="block w-full">
              <img
                src="/rabies.png"
                alt="Coexistence banner"
                className="w-full h-auto object-cover block transition-transform duration-700 ease-out group-hover:scale-[1.01]"
              />
            </Link>
          </ScrollReveal>

        </div>

        {/* Bottom Content Grid */}
        <div className="px-4 sm:px-0 grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 md:gap-10 2xl:gap-12 mt-6 sm:mt-12 2xl:mt-16">

          {/* Headline, Stats & Paragraphs */}
          <div className="md:col-span-2 text-left">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 sm:gap-6 mb-4 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl 2xl:text-6xl !leading-[110%] font-display font-semibold text-gray-900 flex-grow max-w-2xl">
                <WordReveal>
                  True Rescue Begins With Education.
                </WordReveal>
              </h2>

              {/* Stats on the right side of the heading */}
              <div className="flex flex-row gap-6 sm:gap-8 shrink-0 pb-1.5 justify-start lg:justify-end select-none">
                <ScrollReveal
                  as="div"
                  delay={150}
                  className="flex flex-col items-start lg:items-end text-left lg:text-right"
                >
                  <span className="text-brand-orange-700 font-black text-2xl sm:text-3xl lg:text-4xl leading-none">70%+</span>
                  <span className="text-gray-500 uppercase text-[9px] sm:text-[10px] font-bold tracking-wider max-w-[90px] leading-tight mt-1">stray vaccine buffer</span>
                </ScrollReveal>

                <ScrollReveal
                  as="div"
                  delay={250}
                  className="flex flex-col items-start lg:items-end text-left lg:text-right"
                >
                  <span className="text-brand-orange-700 font-black text-2xl sm:text-3xl lg:text-4xl leading-none">100%</span>
                  <span className="text-gray-500 uppercase text-[9px] sm:text-[10px] font-bold tracking-wider max-w-[90px] leading-tight mt-1">preventable rabies disease</span>
                </ScrollReveal>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 2xl:gap-8 text-gray-600 text-xs sm:text-sm 2xl:text-base font-semibold">
              <ScrollReveal delay={200}>
                <p className="leading-relaxed text-justify">
                  Understanding stray behavior is the first step toward peaceful coexistence. Learn about rabies vaccine mitigation, tail postures, guarding spaces, and dedicated feeding strategies to keep your neighborhood safe and friendly.
                </p>
              </ScrollReveal>
              <ScrollReveal delay={350}>
                <p className="leading-relaxed text-justify">
                  Become a proactive community first responder. By capturing brief diagnostic videos and sharing live GPS coordinates, you help our emergency veterinary squad respond efficiently and save stray animal lives instantly.
                </p>
              </ScrollReveal>
            </div>
          </div>

          {/* Button Column - Positioned to the right with compact mobile sizing */}
          <div className="md:col-span-1 flex flex-col justify-end items-end text-right">
            <div className="w-full flex flex-col items-end text-right space-y-3 sm:space-y-4">

              <ScrollReveal delay={450} className="w-full flex justify-end">
                <p className="text-gray-900 font-bold text-xs sm:text-base 2xl:text-lg leading-snug text-right max-w-sm">
                  Host an awareness workshop for your school, college, or residential society.
                </p>
              </ScrollReveal>

              <ScrollReveal delay={550} className="flex flex-col items-end gap-2 w-full">
                <Link
                  to="/awareness"
                  className="btn-collaborate inline-flex w-auto self-end px-4 py-2 text-[11px] xs:text-xs sm:text-sm 2xl:px-8 2xl:py-4 2xl:text-base shadow-sm hover:shadow-md"
                >
                  <span>EXPLORE ALL GUIDES</span>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 2xl:w-6 2xl:h-6 text-white fill-none stroke-current ml-1" viewBox="0 0 24 24" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </Link>
                <Link
                  to="/awareness"
                  className="text-right text-[11px] sm:text-sm font-bold text-brand-orange-700 hover:text-brand-orange-850 hover:underline flex items-center justify-end gap-1 pt-0.5"
                >
                  <span>Register School / RWA Workshop →</span>
                </Link>
              </ScrollReveal>

            </div>
          </div>

        </div>

      </div>
    </section>
  )
}

export default RabiesHub
